import { AufVideoUtils } from 'src/utils/utils';
import { VideoConsoleLogger } from 'src/utils/video-logger';
import { PublishableVideoAdEvents } from '../enums/publishable-events.enum';
import { ImaApi } from '../interfaces/ima-api.inteface';
import { ListenerAction } from '../interfaces/listener-action';
import { Store } from '../store';
import AdPlayer, { AdPlayerOptions } from './_adapter';

const logger = new VideoConsoleLogger();

const API_DEFAULT_SETUP = {
	adsRenderingSettings: {
		enablePreloading: true,
		loadVideoTimeout: 10000
	},
	disableCustomPlaybackForIOS10Plus: true, // IMPORTANT: IOS inline autoplay issues without this
	disableFlashAds: true,
	prerollTimeout: 5000,
	showCountdown: true, // Whether or not to show the ad countdown timer
	vastLoadTimeout: 5000, // As we do prefetch we can try a higher value here,
	locale: undefined,
	numRedirects: undefined
};

const IMA_UNFILLED_ERROR_CODES = {
	1009: 'Empty VAST response.'
};

export interface ImaAdPlayerOptions extends AdPlayerOptions {
	adLabel: string;
	language: string;
}

export class ImaAdPlayer extends AdPlayer {
	public ready = false;
	public api: ImaApi;
	public imaOptions: any;
	private _adLabel: string;
	private _language: string;
	private _store: Store;
	private _alreadyRequested = false;
	private _loadVideoTimeout: number;
	private _prerollTimeout: number;
	private _showCountdown: boolean;
	private _vastLoadTimeout: number;
	private _numRedirects: number;

	private _apiListenersActions: ListenerAction[] = [
		{
			message: (window as any).google.ima.AdEvent.Type.STARTED,
			actionFn: (ad) => {
				// HACK: reforce mute when ad starts to prevent some bugs with 2nd preroll
				if (this._player.muted) {
					this.mute();
				}

				this._emitEventHandler(PublishableVideoAdEvents.AD_START);
				this.publishApiEvent(PublishableVideoAdEvents.AD_PLAY, ad);
			}
		},
		{
			message: (window as any).google.ima.AdEvent.Type.PAUSED,
			actionFn: (ad) =>
				this._emitEventHandler(PublishableVideoAdEvents.AD_PAUSE, ad)
		},
		{
			message: (window as any).google.ima.AdEvent.Type.RESUMED,
			actionFn: (ad) =>
				this._emitEventHandler(PublishableVideoAdEvents.AD_RESUME, ad)
		},
		{
			message: (window as any).google.ima.AdEvent.Type.COMPLETE,
			actionFn: (ad) =>
				this._emitEventHandler(PublishableVideoAdEvents.AD_RESUME, ad)
		},
		{
			message: (window as any).google.ima.AdEvent.Type.CLICK,
			actionFn: (ad) =>
				this._emitEventHandler(PublishableVideoAdEvents.AD_CLICK, ad)
		},
		{
			message: (window as any).google.ima.AdEvent.Type.IMPRESSION,
			actionFn: (ad) =>
				this._emitEventHandler(PublishableVideoAdEvents.AD_IMPRESSION, ad)
		},
		{
			message: (window as any).google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED,
			actionFn: (ad) => {
				this._setAdsState(PublishableVideoAdEvents.AD_END);
				this.publishApiEvent(PublishableVideoAdEvents.AD_CONTENT_RESUME);
			}
		}
	];

	constructor(options: ImaAdPlayerOptions) {
		super({ ...options, name: 'ima' });

		this.name = 'ima';
		this._adLabel = options.adLabel;
		this._language = options.language;

		this.imaOptions = {
			...API_DEFAULT_SETUP,
			id: this._id,
			adLabel: this._adLabel,
			adWillPlayMuted: this._player.muted,
			adsManagerLoadedCallback: this.onApiLoaded.bind(this),
			adsRenderingSettings: this._loadVideoTimeout
				? {
						loadVideoTimeout: this._loadVideoTimeout
				  }
				: API_DEFAULT_SETUP.adsRenderingSettings,
			prerollTimeout: this._prerollTimeout || API_DEFAULT_SETUP.prerollTimeout,
			showCountdown: this._showCountdown || API_DEFAULT_SETUP.showCountdown,
			vastLoadTimeout:
				this._vastLoadTimeout || API_DEFAULT_SETUP.vastLoadTimeout,
			locale:
				(this._language && this._language.toLowerCase()) ||
				API_DEFAULT_SETUP.locale,
			numRedirects: this._numRedirects || API_DEFAULT_SETUP.numRedirects
		};
	}

	public mute(): void {
		const adManager = this.api.getAdsManager;
		if (!(adManager && adManager.setVolume)) return;

		adManager.setVolume(0);
	}

	public play = () => (this.ready ? this.api.resumeAd() : undefined);
	public pause = () => (this.ready ? this.api.pauseAd() : undefined);

	protected async _postInit(store: Store): Promise<void> {
		const getGoogle = async (): Promise<any> => {
			return new Promise((resolve, reject) => {
				const googleReadyInterval = window.setInterval(() => {
					const google = (window as any).google;
					if (google) {
						(window as any).clearInterval(googleReadyInterval);
						return resolve(google);
					}
				}, 50);

				//window.setTimeout(() => window.clearInterval(googleReadyInterval), 2000);
			});
		};

		const google = await getGoogle();

		if (!google || !(this._player.api as any).ima) {
			const err = 'Unable to initialize ad player';
			this.logError(err);
			throw err;
		}

		this._store = store;
		await this._initApi();

		if (!this._hbVideoActive) {
			this._initAdRequest();
		}

		return Promise.resolve();
	}

	protected _postPlayAdInstant = () => this._initAdRequest();

	protected _initApiListeners() {
		this._apiListenersActions.forEach((listener) =>
			this.api.addEventListener(listener.message, listener.actionFn)
		);

		// Listen click / touch events on playback buttons to catch user interaction with player
		AufVideoUtils.addClickListener(
			this._id,
			['ima-play-pause-div', this._id + '_ima-ad-container'],
			() => this.publish('video.player.user.togglePlayPause'),
			{ stopPropagation: true }
		);
	}

	protected _initErrorsListeners() {
		(this._player.api as any).on('adserror', (error) => {
			var errorCode = error.data.AdError.getErrorCode();
			if (IMA_UNFILLED_ERROR_CODES[errorCode]) {
				this.adsState.eventsLists.push('IMA: AD_UNFILLED');
				this.publish('video.ad.unfilled');
			} else {
				this.adsState.eventsLists.push('IMA: AD_ERROR');
				this.logError(error.data.AdError.getMessage() + ' [' + errorCode + ']');
			}
		});
	}

	// Documentation: https://developers.google.com/interactive-media-ads/docs/sdks/html5/v3/apis#ima.AdPodInfo.getPodIndex
	protected _getAdData(data: any) {
		try {
			const ad = data.getAd();

			// Sometimes default infos are not filled, but wrappers are filled instead
			// TODO @money: improve to log more ad data
			return {
				adId: ad.getAdId() || ad.getWrapperAdIds().toString(),
				adSystem: ad.getAdSystem() || ad.getWrapperAdSystems().toString(),
				creativeId: ad.getCreativeId() || ad.getWrapperCreativeIds().toString()
			};
		} catch (e) {
			this.logError('Unable to retrieve some ad data: ' + e.message);
			return;
		}
	}

	public async _initApi(): Promise<void> {
		const that = this;
		const ima = (this._player.api as any).ima;

		if (!this.api && ima && typeof ima === 'function') {
			ima(this.imaOptions);
			this.api = ima;
		}
		logger.info('ima::initApi()', this.api)();

		return Promise.resolve();
	}

	public setApi(api: ImaApi) {
		if (!api) return;

		this.api = api;
	}

	private _initAdRequest() {
		// Autostart on: prefetch ads as soon as possible
		const instance = this._store.getInstance(this._id);
		if (instance && instance.autoplay.startOnSight) {
			this._requestAds();
			return;
		}

		// Autostart off: only load ads when the video player is clicked
		const handler = () => {
			// IMPORTANT: must be done via a user action if autoplay is not allowed
			this.api.initializeAdDisplayContainer(); // Create an ad display container
			this._requestAds();
		};

		(this._player.api as any).on('click', handler);
	}

	// TODO @money: use static constants from IMA SDK
	protected _getVideoBreak(ad: any) {
		let res = '';

		if (!ad || !ad.getAd) {
			logger.debug('ad or ad.getAd not found')();
			return res;
		}

		switch (ad.getAd().getAdPodInfo().getPodIndex()) {
			case 0:
				res = 'preroll';
			case -1:
				res = 'postroll';
			default:
				res = '';
		}

		return res;
	}

	private async _requestAds(): Promise<void> {
		if (this._alreadyRequested) return;

		const adTagUrl = await this.getAdTagUrl();
		this.api.setContentWithAdTag(null, adTagUrl, false);
		this.api.requestAds();

		this._alreadyRequested = true;

		return Promise.resolve();
	}

	private _emitEventHandler(evt: PublishableVideoAdEvents, adData?: any): void {
		this._setAdsState(evt);
		this.publishApiEvent(evt, adData);
	}
}

window.AufVideo = window.AufVideo || {};
window.AufVideo.IMA = ImaAdPlayer;
